import { type PropsWithChildren, type FunctionComponent, useEffect } from 'react'
import { ApolloProvider } from '@apollo/client'
import { IntlProvider } from 'react-intl'
import { QueryParamProvider } from 'use-query-params'
import { Route } from 'react-router'
import { TourProvider } from '@reactour/tour'
import { QueryClientProvider } from '@tanstack/react-query'
import { ThemeProvider, type IPP_SUPPORTED_LOCALE } from '@retailer-platform/shared-components'
import { type TDS_LOCALES } from '@retailer-platform/shared-components/src/tds'
import { fetchDashboardClient } from '../../../legacy/apollo/dashboardClient'
import { makeIntlFormats } from '../../../intl/intl.utils'
import { type DashIntlShape } from '../../../intl/intl.types'
import { trackEvent } from '../../../utils/events/trackEvent'
import { NavProvider } from '../nav/utils/NavProvider'
import { UserFeedbackProvider } from '../user-feedback/UserFeedbackProvider'
import { getDefaultTourProviderProps } from '../../../utils/tour/tour.utils'
import { useTrackPageView } from '../../utils/instrumentation/useTrackPageView'
import { useQueryStaleWhileRevalidateCleanup } from '../../../utils/apollo/makeQueryStaleWhileRevalidate'
import { AdminControlsProvider } from '../../../utils/contexts/admin-controls/AdminControlsContext'
import { BetaEnvBanner } from '../beta-env-banner/BetaEnvBanner'
import { queryClient } from '../../../utils/query-client/queryClient'
import { AppProviderPreloader } from './components/AppProviderPreloader'
import { AppProviderStyling } from './components/AppProviderStyling'

interface Props {
  messages: DashIntlShape['messages']
  locale: IPP_SUPPORTED_LOCALE
}

const tourProviderProps = getDefaultTourProviderProps()

export const AppProvider: FunctionComponent<PropsWithChildren<Props>> = ({
  children,
  messages,
  locale,
}) => {
  useEffect(() => {
    trackEvent({ id: 'app.launched', description: 'The application was launched' })
  }, [])

  useTrackPageView()
  useQueryStaleWhileRevalidateCleanup()

  return (
    <ThemeProvider locale={locale as unknown as TDS_LOCALES}>
      <IntlProvider formats={makeIntlFormats()} locale={locale} messages={messages}>
        <QueryParamProvider ReactRouterRoute={Route}>
          <ApolloProvider client={fetchDashboardClient()}>
            <QueryClientProvider client={queryClient}>
              <NavProvider>
                <UserFeedbackProvider>
                  <AdminControlsProvider>
                    <TourProvider {...tourProviderProps}>
                      <BetaEnvBanner />
                      <AppProviderStyling>
                        <AppProviderPreloader>{children}</AppProviderPreloader>
                      </AppProviderStyling>
                    </TourProvider>
                  </AdminControlsProvider>
                </UserFeedbackProvider>
              </NavProvider>
            </QueryClientProvider>
          </ApolloProvider>
        </QueryParamProvider>
      </IntlProvider>
    </ThemeProvider>
  )
}
